import * as React from 'react'
import { useOutletContext } from 'react-router-dom'

import {
  Button,
  Form,
  Section,
  SelectOptionGroups,
  Text,
} from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { UpsertTimeConditionMutationVariables } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useGetTimeConditions } from 'modules/legal_entities/hooks/timeConditions/useGetTimeConditions'
import type { TimeCondition } from 'modules/legal_entities/hooks/timeConditions/useGetTimeConditions'
import { useUpsertTimeCondition } from 'modules/legal_entities/hooks/timeConditions/useUpsertTimeCondition'
import TimeConditionsLegalEntityForm from 'modules/legal_entities/lib/forms/TimeConditionsLegalEntityForm'
import {
  DEFAULT_DURATION,
  DEFAULT_START_ON,
} from 'modules/legal_entities/lib/forms/TimeConditionsLegalEntityForm'
import {
  DEFAULT_GROUP_TYPE,
  DEFAULT_VIEW_TYPE,
} from 'modules/shift_management/store/internal/models/View'

const TimeConditionsLegalEntityPage = () => {
  const { navigation } = useNavigation()

  const localStorageKey = LOCAL_STORAGE_KEYS.shiftManagementView
  const [localStorage] = useLocalStorage<string>(localStorageKey)
  const viewType = localStorage?.split('/')[0] || DEFAULT_VIEW_TYPE
  const groupType = localStorage?.split('/')[1] || DEFAULT_GROUP_TYPE
  const startOfWeek = moment().startOf('week')

  const [hasSeenCycleCallout, setHasSeenCycleCallout] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.timeBalanceCycle,
    false
  )

  const { legalEntity, loading: loadingLegalEntity } = useOutletContext<{
    legalEntity: LegalEntity
    loading: boolean
  }>()

  const {
    loading: loadingTimeConditions,
    error: errorsTimeConditions,
    timeConditions,
  } = useGetTimeConditions(legalEntity.id)
  const timeCondition: TimeCondition | undefined = timeConditions?.[0] ?? undefined
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const form = useUnsafeMemo(
    () =>
      new TimeConditionsLegalEntityForm({
        id: timeCondition?.id,
        legalEntityId: legalEntity?.id,
        startOn: timeCondition?.startOn ?? DEFAULT_START_ON,
        duration: timeCondition?.duration ?? DEFAULT_DURATION,
      }),
    [legalEntity, timeCondition]
  )

  const startOnField = form.get('startOn')
  const durationField = form.get('duration')

  const { upsertTimeCondition } = useUpsertTimeCondition()
  const onSubmit = () => {
    asyncAction(() => {
      const formValues: UpsertTimeConditionMutationVariables = {
        id: null,
        legalEntityId: Number(legalEntity.id),
        startOn: Number(startOnField.value),
        duration: Number(durationField.value),
      }
      if (timeCondition !== undefined) {
        formValues['id'] = timeCondition.id
      }
      return upsertTimeCondition(formValues)
    })
  }

  const monthsList = useMemo<SelectOptionGroups>(() => {
    return moment.monthsShort().map((month, i) => ({
      value: i + 1,
      label: month,
    }))
  }, [])

  const error = errorsTimeConditions
  const loading = loadingLegalEntity || loadingTimeConditions

  return (
    <ResponsivePage.Body loading={loading} error={error} showHeader={false}>
      <Section.TwoThirds
        illustration="timer"
        title={i18n.t('settings.legal_entities.legal_entity.settings.time_conditions.title')}
        description={i18n.t(
          'settings.legal_entities.legal_entity.settings.time_conditions.description'
        )}
        link={{
          href: navigation.shifts.customViewGroup.insightsRequest.fullPath({
            viewType: viewType,
            groupType: groupType,
            year: startOfWeek.year(),
            month: startOfWeek.month() + 1,
            day: startOfWeek.day(),
          }),
          label: i18n.t('settings.legal_entities.legal_entity.settings.time_conditions.redirect'),
        }}
        banner={
          !hasSeenCycleCallout && (
            <Callout
              type="info"
              title={i18n.t(
                'settings.legal_entities.legal_entity.settings.time_conditions.help.title'
              )}
              description={i18n.t(
                'settings.legal_entities.legal_entity.settings.time_conditions.help.description'
              )}
              onRemove={() => setHasSeenCycleCallout(true)}
            />
          )
        }
      >
        <Form onSubmit={onSubmit} withBackground>
          <Text.Normal color="grey800" size="300" weight="medium">
            {i18n.t('settings.legal_entities.legal_entity.settings.time_conditions.cycle.title')}
          </Text.Normal>

          <SelectField
            label={i18n.t(
              'settings.legal_entities.legal_entity.settings.time_conditions.cycle.start_on'
            )}
            options={monthsList}
            value={startOnField.value}
            onChange={(startOn) => startOnField.set(startOn)}
            showOriginalLabelText
          />

          <TextField
            label={i18n.t(
              'settings.legal_entities.legal_entity.settings.time_conditions.cycle.duration'
            )}
            value={i18n.t(
              'settings.legal_entities.legal_entity.settings.time_conditions.cycle.duration_months',
              { months: durationField.value }
            )}
            onChange={(duration) => durationField.set(duration)}
            readonly
          />

          {form.isDirty && (
            <Form.Buttons>
              <Button disabled={!form.isValid} submit>
                {i18n.t('common.submit')}
              </Button>
            </Form.Buttons>
          )}
        </Form>
      </Section.TwoThirds>
    </ResponsivePage.Body>
  )
}

export default observer(TimeConditionsLegalEntityPage)
